import { Upload as BaseUpload, Spin, message } from 'antd';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import {
  ACTION,
  DEFAULT_ACCEPT,
  MAX_TASK_COUNT,
  SIZE100MB,
  geneHeaders,
} from '../const';
import { IUploadWithCustomAttrs } from '../type';
import { getBase64, isImage } from '../utils';

export interface IProps extends UploadProps {
  module: string;
  finished?: (fileList: IUploadWithCustomAttrs[]) => void;
  restCount?: number;
}

export const UploadEntrance: FC<IProps> = (props) => {
  const {
    children,
    finished,
    accept = DEFAULT_ACCEPT,
    module = '',
    multiple = true,
    restCount = MAX_TASK_COUNT,
    showUploadList = false,
    action = ACTION,
    headers = geneHeaders(),
    ...rest
  } = props;
  const [files, setFiles] = useState<IUploadWithCustomAttrs[]>([]);
  const [isFirst, setIsFirst] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const judgeImageSize = useRef<boolean>(false);
  const imageSizeMT100 = useRef<boolean>(false);
  const handledCount = useRef<number>(0);

  const calImageSizes = (list: RcFile[]) => {
    return list.reduce((pre, cur) => pre + cur.size, 0);
  };

  const reset = () => {
    setIsUploading(false);
    setIsFirst(true);
    setFiles([]);
  };

  const onChange = (value: { file: UploadFile; fileList: UploadFile[] }) => {
    const { file, fileList } = value;
    if (isFirst) {
      setIsFirst(false);
      setIsUploading(true);
    }
    setFiles([...fileList]);
    if (
      file.status === 'error' ||
      (file.response && file.response.code !== 0)
    ) {
      message.error('文件上传失败，请尝试重新上传');
    }
    const allUploaded = fileList.every((file) => file.status !== 'uploading');
    if (allUploaded) {
      finished?.(fileList);
      reset();
    }
  };

  const addHandledCount = (total: number) => {
    handledCount.current = handledCount.current + 1;
    if (handledCount.current === total) {
      judgeImageSize.current = false;
      imageSizeMT100.current = false;
      handledCount.current = 0;
    }
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (!isImage(file.type) || module === 'OCR' || module === 'API') {
      if (file.size > SIZE100MB) {
        message.error('每份文件大小不得超过100M');
        addHandledCount(fileList.length);
        return BaseUpload.LIST_IGNORE;
      }
    } else {
      if (!judgeImageSize.current) {
        judgeImageSize.current = true;
        const imageList = fileList.filter((v) => isImage(v.type));
        imageSizeMT100.current = calImageSizes(imageList) > SIZE100MB;
        if (imageSizeMT100.current) {
          message.error('图片文件总大小不得超过100M');
          addHandledCount(fileList.length);
          return BaseUpload.LIST_IGNORE;
        }
      } else {
        if (imageSizeMT100.current) {
          addHandledCount(fileList.length);
          return BaseUpload.LIST_IGNORE;
        }
      }
    }

    return new Promise<File>((resolve, reject) => {
      addHandledCount(fileList.length);
      const _file = file as RcFile & { _preview: string };
      const reader = getBase64(file);
      reader.onload = () => {
        _file._preview = reader.result as string;
        resolve(_file);
      };
      reader.onerror = () => {
        reject();
      };
      resolve(file);
    });
  };

  return (
    <BaseUpload
      disabled={isUploading || restCount === 0}
      {...rest}
      headers={headers}
      multiple={multiple}
      accept={accept}
      action={action}
      fileList={files}
      onChange={onChange}
      beforeUpload={beforeUpload}
      showUploadList={showUploadList}
    >
      <Spin tip="上传中..." size="small" spinning={isUploading}>
        {children}
      </Spin>
    </BaseUpload>
  );
};
